import React, { FC } from 'react';
import { render, useServerData } from '@xingternal/hops';
import {
  XingFrameProvider,
  LOGIN_STATE,
} from '@xingternal/xing-frame-provider';
import { ErrorContainer } from '@xingternal/content-error';
import { initSentry } from '@/utils/sentry';
import GlobalStyles from '@xingternal/global-styles';
import navigationData from '@xingternal/xing-frame-provider/data/navigation';
import withApolloData from '@xingternal/xing-frame-provider/withApolloData';
import withStaticData from '@xingternal/xing-frame-provider/withStaticData';

import { ROUTES } from './routes';
import { cache, links } from './utils/graphql';

import { Router } from './routes/Router';
import { CompaniesDataProvider } from './contexts/CompaniesDataContext';
import LoggedOutPage from './components/LoggedOutPage';
import LoggedInPage from './components/LoggedInPage';
import { ErrorBoundary } from './components/ErrorBoundary';

type RoutesPages = {
  companies: string;
  followedCompanies: string;
  managedCompanies: string;
  mostVisitedCompanies: string;
  recentlyClaimedCompanies: string;
  industries: string;
  industry: string;
};

type FrameProviderProps = {
  internalPaths?: string[];
  children: React.ReactNode;
  activeItem: string;
};
const FrameProvider: React.FC<FrameProviderProps> = (props) => {
  const { loginState } = useServerData();

  const Component =
    loginState !== LOGIN_STATE.loggedOut
      ? withApolloData(XingFrameProvider)
      : withStaticData({ navigationData })(XingFrameProvider);

  return <Component {...props} loginState={loginState} />;
};

initSentry();

const internalPaths: string[] = Object.keys(ROUTES as RoutesPages).map(
  (key) => ROUTES[key as keyof RoutesPages],
);

export const App: FC = () => {
  const { authenticated } = useServerData();

  const AppComponent = authenticated ? LoggedInPage : LoggedOutPage;

  return (
    <ErrorBoundary>
      <ErrorContainer>
        <FrameProvider internalPaths={internalPaths} activeItem="companies">
          <GlobalStyles>
            <CompaniesDataProvider>
              <AppComponent>
                <Router />
              </AppComponent>
            </CompaniesDataProvider>
          </GlobalStyles>
        </FrameProvider>
      </ErrorContainer>
    </ErrorBoundary>
  );
};

export default render(<App />, {
  graphql: {
    cache,
    links,
  },
});
